// ---------------------------
// 🔹 Sales Grouping & Summary Utils
// ---------------------------

import Order from "../models/Order";
import type BookedRoom from "../models/BookedRoom";
import { BookingSummary, SaleItem, } from "../types/sales";
import { sendRequest } from "./api";

/**
 * Groups order items by service type.
 * - allSales = paid + pending merged
 * - paidSales = paid only
 * - pendingSales = pending only
 */
export async function groupOrderItemsByType(orders: Order[]) {
  const serviceTypes: ServiceType[] = ["drinks", "foods", "laundries", "games"];

  const result: Record<
    ServiceType,
    {
      summary: {
        cash: number;
        pos: number;
        transfer: number;
        pending: number;
        online: number;
        paidDiscount: number;
        unPaidDiscount: number;
        soldQuantity: number;
        totals: number;
      };
      paidSales: SaleItem[];
      pendingSales: SaleItem[];
      allSales: SaleItem[];
    }
  > = Object.fromEntries(
    serviceTypes.map((type) => [
      type,
      {
        summary: {
          cash: 0,
          pos: 0,
          transfer: 0,
          online: 0,
          pending: 0,
          paidDiscount: 0,
          unPaidDiscount: 0,
          soldQuantity: 0,
          totals: 0,
        },
        paidSales: [],
        pendingSales: [],
        allSales: [],
      },
    ])
  ) as any;

  for (const order of orders) {
    const isPaid = order.isPaid ?? false;
    const paymentType = order.paymentMethod?.toLowerCase?.() || "";
    const discount = order.discount ?? 0;

    for (const item of order.orderItems || []) {
      const type = (item.itemType ?? "").toLowerCase() as ServiceType;
      if (!serviceTypes.includes(type)) continue;

      const category = result[type];

      const id = item.itemId?.trim() || "unknown";
      const name = item.itemName ?? "Unknown";
      const qty = item.quantity || 1;
      const amt = item.amount ?? 0;

      /* ---------------- SUMMARY ---------------- */
      if (isPaid) {
        if (paymentType === "cash") category.summary.cash += amt;
        else if (paymentType === "pos") category.summary.pos += amt;
        else if (paymentType === "transfer") category.summary.transfer += amt;
        else if (paymentType === "online") category.summary.online += amt;

        // category.summary.totals += amt;
        category.summary.paidDiscount += discount;

        // category.summary.soldQuantity += qty;

      } else {
        category.summary.pending += amt - discount;
        category.summary.unPaidDiscount += discount;
      }

      category.summary.soldQuantity += qty;
      category.summary.totals += amt;

      const stockIn = item?.stock?.stockIn ?? 0;
      const stockOut = item?.stock?.stockOut ?? 0;
      const opening = item?.stock?.opening ?? 0;
      const closing = opening;

      /* ---------------- ALL SALES (MERGED) ---------------- */
      let allRecord = category.allSales.find((r) => r.id === id);
      if (!allRecord) {
        allRecord = {
          id,
          createdAt: item.createdAt as string,
          name,
          unitPrice: 0,
          category: type,
          paymentStatus: "Mixed",
          stockIn,
          stockOut,
          opening,
          closing: item?.stock?.closing ?? 0,
          quantitySold: 0,
          amount: 0,
        };
        category.allSales.push(allRecord);
      }

      allRecord.quantitySold += qty;
      allRecord.closing -= qty;
      allRecord.amount += amt;
      allRecord.unitPrice = allRecord.amount / allRecord.quantitySold;

      /* ---------------- PAID / PENDING (SEPARATE) ---------------- */
      const targetList = isPaid ? category.paidSales : category.pendingSales;

      let record = targetList.find((r) => r.id === id);
      if (!record) {
        record = {
          id,
          createdAt: item.createdAt as string,
          name,
          unitPrice: 0,
          closing: item?.stock?.closing ?? 0,
          category: type,
          paymentStatus: isPaid ? "Paid" : "Pending",
          stockIn,
          stockOut,
          opening,
          quantitySold: 0,
          amount: 0,
        };
        targetList.push(record);
      }
      record.closing -= qty;;
      record.quantitySold += qty;
      record.amount += amt;
      record.unitPrice = record.amount / record.quantitySold;
    }

  }

  return {
    drinks: result.drinks,
    foods: result.foods,
    laundries: result.laundries,
    games: result.games,
  };
}

// -----------------------------------
// 🔹 Booking Summary (Rooms)
// -----------------------------------
export function computeBookingSummary(bookings: BookedRoom[]): BookingSummary {
  return bookings.reduce<BookingSummary>((acc, booking) => {

    const total = booking.amount ?? 0;
    const isPaid = booking.isPaid ?? false;
    const paymentType = booking.paymentMethod?.toLowerCase?.() || "";
    const isFullTime = booking.bookingType === "fullTime"

    if (isPaid) {
      acc.bookDiscount += booking.discount ?? 0;
      // if (isFullTime) acc.guestCount += 1;
      if (paymentType === "cash") acc.bookCash += total;
      else if (paymentType === "transfer") acc.bookTransfer += total;
      else if (paymentType === "pos") acc.bookPos += total;
      else if (paymentType === "online") acc.bookOnline += total;
    } else {
      acc.bookPending += total;
      acc.bookUnpaidDiscount += booking.discount ?? 0;
    }

    if (isFullTime) acc.guestCount += 1;

    return acc;
  }, {
    bookCash: 0,
    bookTransfer: 0,
    bookPending: 0,
    bookOnline: 0,
    bookDiscount: 0,
    bookUnpaidDiscount: 0,
    bookPos: 0,
    guestCount: 0,
  });
}

// -----------------------------------
// 🔹 Merge Booking & Order Summaries (Updated)
// -----------------------------------
export function mergeSummaries(
  summaries: {
    rooms?: BookingSummary;
    foods?: any;
    drinks?: any;
    games?: any;
    laundries?: any;
  }
) {
  const { rooms, foods, drinks, games, laundries } = summaries;

  // Utility function to safely extract numeric fields
  const safe = (value: any) => (typeof value === "number" ? value : 0);

  // Compute totals for all categories except rooms
  const orderCategories = { foods, drinks, games, laundries };

  // console.log(orderCategories)

  let totalOrderCash = 0;
  let totalOrderPos = 0;
  let totalOrderTransfer = 0;
  let totalOrderOnline = 0;
  let totalOrderPending = 0;

  let totalOrderSales = 0;

  for (const s of Object.values(orderCategories)) {
    if (!s) continue;
    totalOrderCash += safe(s.cash);
    totalOrderPos += safe(s.pos);
    totalOrderTransfer += safe(s.transfer);
    totalOrderOnline += safe(s.online);
    totalOrderPending += safe(s.pending);
    // totalOrderDiscount += safe(s.unPaidDiscount + s.paidDiscount);
    // totalOrderUnpaidDiscount += safe(s.unPaidDiscount);
    // totalOrderPaidDiscount += safe(s.paidDiscount);
    totalOrderSales += safe(s.totals);
  }

  // Booking (rooms)
  const totalBookingAmount = (
    safe(rooms?.bookCash) + safe(rooms?.bookTransfer) + 
    safe(rooms?.bookPos) + safe(rooms?.bookOnline)
  );
  
  const totalBookingPending = safe(rooms?.bookPending);
  const totalBookingDiscount = safe(rooms?.bookDiscount);
  const totalBookingUnpaidDiscount = safe(rooms?.bookUnpaidDiscount);

  // Combine both sides
  const grossSales = totalOrderSales + totalBookingAmount + totalBookingPending;
  const totalUnpaidDiscount = totalBookingUnpaidDiscount;

  // console.log({ grossSales, totalBookingAmount, totalOrderSales, totalBookingPending})

  // ✅ Unified final structure
  return {
    totals: {
      rooms: (
        totalBookingAmount + totalBookingPending
      ),
      foods: safe(foods?.totals),
      drinks: safe(drinks?.totals),
      laundries: safe(laundries?.totals),
      games: safe(games?.totals),
    },
    quantities: {
      rooms: safe(rooms?.guestCount),
      foods: safe(foods?.soldQuantity),
      drinks: safe(drinks?.soldQuantity),
      laundries: safe(laundries?.soldQuantity),
      games: safe(games?.soldQuantity),
    },
    //  totalDiscount: totalBookingDiscount,
    totalDiscount: totalBookingDiscount + totalUnpaidDiscount,
    grossSales,
    cash: totalOrderCash + safe(rooms?.bookCash),
    pos: totalOrderPos + safe(rooms?.bookPos),
    transfer: totalOrderTransfer + safe(rooms?.bookTransfer),
    online: totalOrderOnline + safe(rooms?.bookOnline),
    pending: totalOrderPending + totalBookingPending,
    expenses: 0,
  };
}


/**
 * Returns filtered sales (paid, pending, or all) for a given category.
 * @param allOrderSales - The complete grouped order sales object
 * @param category - One of: "foods" | "drinks" | "laundries" | "games"
 * @param filter - One of: "paid" | "pending" | "all"
 */
export function getFilteredSales(
  allOrderSales: Record<
    string,
    {
      allSales: SaleItem[]
      paidSales: SaleItem[];
      pendingSales: SaleItem[],
    }
  >,
  category: string,
  filter: string
): SaleItem[] {
  const categoryData = allOrderSales[category];
  if (!categoryData) return [];

  const { allSales, paidSales, pendingSales } = categoryData;
  const lowerFilter = filter.toLowerCase();

  if (lowerFilter === "pending") return pendingSales;
  if (lowerFilter === "paid") return paidSales;

  // return [...paidSales, ...pendingSales];
  return allSales;
}


export const getExpenseByDate = async (terminal: string, dateRange?: { start?: string; end?: string }) => {
  try {
    // Build query params
    const params = new URLSearchParams();
    if (dateRange?.start) params.append("start", dateRange.start);
    if (dateRange?.end) params.append("end", dateRange.end);
    params.append("terminal", terminal);

    // Fetch data from API
    const response = await sendRequest("GET", `/expenditures?${params.toString()}`);

    return response;

  } catch (err: any) {
    console.error(err);
  }
};